// src/components/DpcTopBar.tsx (2026-07-01)
// DPC分析タブ: 6桁~14桁の集計レベルを「タブ」として表の直上に置くだけの軽量バー。
// フィルタ類は折りたたみ可能なサイドバー(FilterBar の isDisease ブロック)に戻したので、
// ここは桁切替タブのみ(サイドバー集計レベルは削除しこのタブに一本化)。
// [2026-09-28 ユーザ指示] 7桁目(病態等)・8桁目(年齢・JCS等)・13桁目(副傷病) のタブを追加。
//   疾患を選んでいるときは、その疾患で分岐しない階層のタブを薄く表示する
//   (押しても 1 行しか出ない = 見ても意味がない、を押す前に分かるように)。押すことはできる。
import { useQuery } from "@tanstack/react-query";
import { useFilters } from "../store";
import type { DpcBranchLevelsResponse } from "@shared/api";
import { DPC_LEVEL_META } from "../lib/dpcLevels";
import { diseasePeriod } from "../lib/useDiseaseRanking";

function useDpcBranchLevels(prefix: string | null, dpcfy: number | null, dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: ["dpc_branch_levels", prefix, dpcfy, dateFrom, dateTo],
    enabled: !!prefix && prefix.length >= 6,
    queryFn: async () => {
      // 期間は主表 (useDiseaseRanking) と同じ実効期間。行クリックの飛び先 (next_level) と同じ集合で判定させる。
      const p = new URLSearchParams({ prefix: prefix!, dateFrom, dateTo });
      if (dpcfy != null) p.set("dpcfy", String(dpcfy));
      const r = await fetch(`/api/query/dpc_branch_levels?${p.toString()}`, { credentials: "include" });
      if (!r.ok) throw new Error(`dpc_branch_levels ${r.status}`);
      return r.json() as Promise<DpcBranchLevelsResponse>;
    },
    staleTime: 10 * 60_000,
  });
}

export function DpcTopBar() {
  const f = useFilters();
  const def = diseasePeriod(f.asOfMonth, f.dpcfy, f.diseaseAutoPeriod);
  const { data } = useDpcBranchLevels(f.dpc6, f.dpcfy, f.dateFromOverride ?? def.dateFrom, f.dateToOverride ?? def.dateTo);
  const branchLevels = f.dpc6 && data?.prefix === f.dpc6 ? new Set(data.levels) : null;
  return (
    <div className="flex gap-1 border-b border-slate-200 overflow-x-auto whitespace-nowrap px-1">
      {DPC_LEVEL_META.map((l) => {
        const active = f.dpcLevel === l.v;
        // 6 桁 (疾患別) と 14 桁 (完全) は常に通常表示。間の階層は選択中の疾患で分岐が無ければ薄く。
        const noBranch = branchLevels != null && l.v !== 6 && l.v !== 14 && !branchLevels.has(l.v);
        return (
          <button key={l.v} onClick={() => f.setDpcLevel(l.v)}
            title={l.desc + (noBranch ? "\n※選択中の疾患ではこの桁は分岐しません (1 行だけになります)" : "")}
            className={[
              "px-3 py-2 text-sm border-b-2 shrink-0 transition-colors",
              active ? "border-brand text-brand font-semibold bg-emerald-50/60"
                     : noBranch ? "border-transparent text-slate-300 hover:bg-slate-50"
                                : "border-transparent text-slate-600 hover:bg-slate-50",
            ].join(" ")}>
            {l.tab}<span className={`text-[10px] ml-1 ${noBranch ? "text-slate-300" : "text-slate-400"}`}>{l.sub}</span>
          </button>
        );
      })}
    </div>
  );
}
